import useLanguage from "../i18n/useLanguage";
import React, { useState, useRef, useEffect } from "react";
import PortfolioCard from "./PortfolioCard.jsx";
import { portfolio as defaultPortfolio } from "../data/portfolio";
import { fetchPortfolios } from "../api/client";

export default function Portfolio() {
  const { t } = useLanguage();
  const INITIAL_COUNT = 4;

  const [portfolioList, setPortfolioList] = useState(defaultPortfolio);
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);
  const sectionRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    fetchPortfolios(defaultPortfolio).then((data) => {
      if (isMounted && data && data.length > 0) {
        setPortfolioList(data);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const isAllVisible = visibleCount >= portfolioList.length;

  /* ================= HANDLER ================= */
  const handleClick = () => {
    if (isAllVisible) {
      // collapse
      setVisibleCount(INITIAL_COUNT);
      sectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      // expand
      setVisibleCount((prev) => {
        const next = prev + INITIAL_COUNT;

        // auto scroll setelah DOM update
        setTimeout(() => {
          gridRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }, 150);

        return next;
      });
    }
  };

  return (
    <section
      ref={sectionRef}
      id="portfolio"
      className="min-h-screen bg-black text-white flex flex-col items-center justify-center w-full py-20 overflow-x-hidden"
    >
      {/* ================= TITLE ================= */}
      <div className="max-w-2xl mx-auto text-center mb-16">
        <h2 className="text-6xl font-bold text-amber-300 mb-4">{t("Our Portfolio")}{" "}</h2>
      </div>

      {/* ================= GRID ================= */}
      <div
        ref={gridRef}
        className="
    grid
    grid-cols-1
    md:grid-cols-2
    gap-x-[69px]
    gap-y-[39px]
    px-4 md:px-8
    transition-all duration-700 ease-out
  "
      >
        {portfolioList.slice(0, visibleCount).map((item, index) => (
          <div
            key={index}
            className="
              transition-all duration-700 ease-out
              opacity-100 translate-y-0
              hover:scale-[1.02]
            "
          >
            <PortfolioCard {...item} />
          </div>
        ))}
      </div>

      {/* ================= ARROW BUTTON ================= */}
      <button
        onClick={handleClick}
        aria-label={t(isAllVisible ? "Collapse portfolio" : "Load more portfolio")}
        className="
    group
    relative
    mt-20
    flex items-center justify-center
    transition-transform duration-300
    hover:translate-y-1
  "
      >
        {/* Hover shadow */}
        {isAllVisible ? <ArrowHoverShadowUp /> : <ArrowHoverShadowDown />}

        {/* Arrow + text wrapper */}
        <div className="relative w-[90px] h-[26px] z-10 group-hover:scale-105 transition-transform duration-300">
          {/* Arrow */}
          {isAllVisible ? <ArrowUp /> : <ArrowDown />}

          {/* Text inside arrow */}
          <span
            className={`
    absolute inset-0
    flex items-center justify-center
    font-bold
    text-[12px]
    tracking-[0.1em]
    text-amber-300
    pointer-events-none whitespace-nowrap

    opacity-0
    transition-all duration-300 ease-out

    ${
      isAllVisible
        ? // LESS → TURUN
          "translate-y-[6px] group-hover:opacity-100 group-hover:translate-y-[35px]"
        : // MORE → NAIK
          "-translate-y-[6px] group-hover:opacity-100 group-hover:-translate-y-[35px]"
    }
  `}
          >
            {t(isAllVisible ? "Less" : "More").toUpperCase()}
          </span>
        </div>
      </button>
    </section>
  );
}

/* ================= SVG COMPONENTS ================= */

function ArrowDown() {
  return (
    <svg width="90" height="26" viewBox="0 0 90 26" fill="none">
      <path
        d="M3 3L45 23L87 3"
        stroke="url(#gradDown)"
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient id="gradDown" x1="45" y1="-6" x2="45" y2="32">
          <stop stopColor="#FFE53A" />
          <stop offset="1" stopColor="#926105" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function ArrowUp() {
  return (
    <svg width="90" height="26" viewBox="0 0 90 26" fill="none">
      <path
        d="M3 23L45 3L87 23"
        stroke="url(#gradUp)"
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient id="gradUp" x1="45" y1="32" x2="45" y2="-6">
          <stop stopColor="#FFE53A" />
          <stop offset="1" stopColor="#926105" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function ArrowHoverShadowDown() {
  return (
    <svg
      className="
        absolute
        left-1/2 top-1/2
        -translate-x-[570px] -translate-y-[93px]
        w-[1128px] h-[107px]
        opacity-0 scale-95
        transition-all duration-500 ease-out
        group-hover:opacity-100
        group-hover:scale-100
        pointer-events-none whitespace-nowrap
        hidden md:block
      "
      viewBox="0 0 1128 107"
      fill="none"
    >
      <path
        d="M571.889 106.436L512.389 72.0833H110.889C110.889 72.0833 -138.611 38.0831 110.889 15.0831C360.389 -7.91691 663.889 -1.91588 984.889 15.0831C1305.89 32.0821 984.889 72.0833 984.889 72.0833L631.389 72.0831L571.889 106.436Z"
        fill="url(#paintDown)"
      />
      <defs>
        <radialGradient
          id="paintDown"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(572.889 -35.918) rotate(88.9919) scale(142.022 2721.39)"
        >
          <stop stopOpacity="0" />
          <stop offset="0.610577" stopColor="#FFEE00" stopOpacity="0.05" />
          <stop offset="0.971154" stopColor="#FFE600" stopOpacity="0.2" />
        </radialGradient>
      </defs>
    </svg>
  );
}

function ArrowHoverShadowUp() {
  return (
    <svg
      className="
        absolute
        left-1/2 top-1/2
        -translate-x-[557px] -translate-y-[14px]
        w-[1128px] h-[107px]
        opacity-0 scale-95
        transition-all duration-500 ease-out
        group-hover:opacity-100
        group-hover:scale-100
        pointer-events-none whitespace-nowrap
        hidden md:block
      "
      viewBox="0 0 1128 107"
      fill="none"
    >
      <path
        d="M555.667 0L615.167 34.3527H1016.67C1016.67 34.3527 1266.17 68.353 1016.67 91.3529C767.167 114.353 463.667 108.352 142.667 91.3529C-178.333 74.3539 142.667 34.3527 142.667 34.3527L496.166 34.3529L555.667 0Z"
        fill="url(#paintUp)"
      />
      <defs>
        <radialGradient
          id="paintUp"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(554.667 142.354) rotate(-91.0081) scale(142.022 2721.39)"
        >
          <stop stopOpacity="0" />
          <stop offset="0.610577" stopColor="#FFEE00" stopOpacity="0.05" />
          <stop offset="0.971154" stopColor="#FFE600" stopOpacity="0.2" />
        </radialGradient>
      </defs>
    </svg>
  );
}
